// Data model for the Hem floor-plan editor: walls, fixtures (wardrobes/appliances
// — each also a trackable "pryl") and text labels, drawn in a fixed design space.

export const PLAN_W = 800;
export const PLAN_H = 1120;
export const PLAN_VIEWBOX = `0 0 ${PLAN_W} ${PLAN_H}`;
export const GRID = 10; // snap step

export interface Wall {
  id: string;
  x1: number; y1: number; x2: number; y2: number;
  balcony?: boolean; // balcony walls are drawn but excluded from the m² estimate
  door?: boolean;    // a doorway: drawn as a door symbol, but still closes a room for the m² calc
}

// A window set into an ordinary wall: both ends lie on the same wall.
export interface WindowOpening {
  id: string;
  x1: number; y1: number; x2: number; y2: number;
}

// Paint per wall side: `p` = the side the wall's normal (-dy, dx) points to,
// `m` = the other side. One drawn wall often runs past several rooms, so each
// side holds painted stretches: a..b = 0..1 along the wall, c = hex colour.
// Unpainted stretches are plain white.
export interface PaintSeg { a: number; b: number; c: string; }
export type WallColors = Record<string, { p?: PaintSeg[]; m?: PaintSeg[] }>;

// Paint a..b with colour c (null erases), replacing whatever was there
export const applyPaint = (segs: PaintSeg[] = [], a: number, b: number, c: string | null): PaintSeg[] => {
  const out: PaintSeg[] = [];
  for (const s of segs) {
    if (s.b <= a || s.a >= b) { out.push(s); continue; }
    if (s.a < a) out.push({ a: s.a, b: a, c: s.c });
    if (s.b > b) out.push({ a: b, b: s.b, c: s.c });
  }
  if (c) out.push({ a, b, c });
  return out.sort((x, y) => x.a - y.a);
};

// Older saves stored one colour for the whole side
const normPaint = (v: any): PaintSeg[] | undefined => {
  if (typeof v === "string") return [{ a: 0, b: 1, c: v }];
  if (!Array.isArray(v)) return undefined;
  return v
    .filter((sg) => sg && typeof sg.c === "string" && Number.isFinite(Number(sg.a)) && Number.isFinite(Number(sg.b)))
    .map((sg) => ({ a: Number(sg.a), b: Number(sg.b), c: sg.c }));
};

export interface TextLabel {
  id: string;
  text: string;
  x: number; y: number;
}

export interface Fixture {
  id: string;
  kind: string;   // key into FIXTURE_KINDS
  label: string;  // shown on the plan
  x: number; y: number; w: number; h: number;
  rot: number;    // rotation in degrees (0/90/180/270 via the rotate handle)
  color?: string; // hex colour painted in the 3D view (fabric, cabinet fronts…)
  topColor?: string; // worktop surface colour (bänkskiva/diskbank), painted separately
  elev?: number;  // cm from the floor, for wall-mounted kinds (vägghylla)
  // tracking fields (a fixture is also a pryl)
  brand: string;
  model: string;
  lastReplaced: string;   // "YYYY-MM"
  intervalMonths: string; // optional replacement interval
  notes: string;
}

export interface MoodItem {
  id: string;
  kind: "image" | "color" | "note";
  value: string; // image URL, #hex colour or note text
  link?: string; // original page the image came from (optional)
}

export interface Room {
  id: string;
  name: string;
  items: MoodItem[];
}

export const ROOM_SUGGESTIONS = ["Vardagsrum", "Sovrum", "Kök", "Badrum", "Hall", "Arbetsrum", "Balkong"];

// A saved distance measurement drawn between two points on the plan.
// `label` holds a manually entered real-world value (e.g. "2,35 m") that
// overrides the scale-derived length; empty means "show the computed length".
export interface Measure {
  id: string;
  x1: number; y1: number;
  x2: number; y2: number;
  label?: string;
}

export interface Home {
  planUrl: string;
  planRot: number; // background-image rotation in degrees (to straighten a skewed scan)
  planHidden: boolean; // whether the background image is hidden (persisted)
  walls: Wall[];
  windows: WindowOpening[];
  wallColors: WallColors;
  fixtures: Fixture[];
  labels: TextLabel[];
  scale: number; // design units per metre (0 = not calibrated)
  rooms: Room[]; // one moodboard per room
  measures: Measure[]; // saved distance measurements
  measuresHidden: boolean; // whether measurements are hidden (persisted)
  actualArea: number; // real total floor area in m² the user entered (0 = none)
}

export interface FixtureKind {
  key: string;
  label: string;
  abbr: string;   // short text drawn in the box (architectural-plan style)
  w: number;      // fallback size in design units (used when uncalibrated)
  h: number;
  wcm: number;    // real-world size in cm (used once the plan is calibrated)
  hcm: number;
  track: boolean; // does "senast bytt"/reminders make sense?
}

// Kinds drawn as line symbols instead of a text abbreviation.
export const DRAWN_KINDS = new Set(["spis", "wc", "handfat", "dusch", "badkar", "element", "brandvarnare", "hornsoffa", "ugnmicro", "diskbank", "bankskiva"]);
// Kinds that draw their own outline — skip the default bounding-box rect.
export const NO_BOX = new Set(["hornsoffa"]);
// Overlay kinds: a see-through surface (e.g. a worktop) drawn on top of other
// fixtures, outline only, so appliances beneath stay visible and clickable.
export const OVERLAY = new Set(["bankskiva"]);

// Palette of placeable fixtures. Sizes are in design units.
export const FIXTURE_KINDS: FixtureKind[] = [
  { key: "garderob", label: "Garderob", abbr: "G", w: 70, h: 45, wcm: 100, hcm: 60, track: true },
  { key: "kladkammare", label: "Klädkammare", abbr: "KLK", w: 80, h: 70, wcm: 130, hcm: 110, track: false },
  { key: "kyl", label: "Kyl", abbr: "K", w: 55, h: 55, wcm: 60, hcm: 65, track: true },
  { key: "frys", label: "Frys", abbr: "F", w: 55, h: 55, wcm: 60, hcm: 65, track: true },
  { key: "diskmaskin", label: "Diskmaskin", abbr: "DM", w: 55, h: 55, wcm: 60, hcm: 60, track: true },
  { key: "spis", label: "Spis", abbr: "", w: 55, h: 55, wcm: 60, hcm: 60, track: true },
  { key: "ugn", label: "Ugn", abbr: "U", w: 55, h: 55, wcm: 60, hcm: 60, track: true },
  { key: "micro", label: "Micro", abbr: "MIC", w: 40, h: 28, wcm: 50, hcm: 35, track: true },
  { key: "ugnmicro", label: "Ugn/Micro", abbr: "", w: 48, h: 62, wcm: 60, hcm: 85, track: true },
  { key: "diskbank", label: "Diskbänk", abbr: "", w: 95, h: 45, wcm: 120, hcm: 60, track: false },
  { key: "bankskiva", label: "Bänkskiva", abbr: "", w: 160, h: 45, wcm: 200, hcm: 60, track: false },
  { key: "tvattmaskin", label: "Tvättmaskin", abbr: "TM", w: 55, h: 55, wcm: 60, hcm: 60, track: true },
  { key: "torktumlare", label: "Torktumlare", abbr: "TT", w: 55, h: 55, wcm: 60, hcm: 60, track: true },
  { key: "wc", label: "WC", abbr: "", w: 45, h: 60, wcm: 40, hcm: 65, track: false },
  { key: "handfat", label: "Handfat", abbr: "", w: 50, h: 35, wcm: 55, hcm: 45, track: false },
  { key: "dusch", label: "Dusch", abbr: "", w: 60, h: 60, wcm: 90, hcm: 90, track: false },
  { key: "badkar", label: "Badkar", abbr: "", w: 120, h: 55, wcm: 170, hcm: 75, track: false },
  { key: "elskap", label: "Elskåp", abbr: "EL", w: 45, h: 45, wcm: 40, hcm: 40, track: true },
  { key: "element", label: "Element", abbr: "", w: 80, h: 16, wcm: 100, hcm: 15, track: false },
  { key: "brandvarnare", label: "Brandvarnare", abbr: "", w: 30, h: 30, wcm: 12, hcm: 12, track: true },
  { key: "tv", label: "TV", abbr: "TV", w: 80, h: 16, wcm: 110, hcm: 10, track: true },
  { key: "soffa", label: "Soffa", abbr: "SOFFA", w: 130, h: 55, wcm: 200, hcm: 90, track: true },
  { key: "hornsoffa", label: "Hörnsoffa", abbr: "", w: 130, h: 120, wcm: 250, hcm: 200, track: true },
  { key: "sang", label: "Säng", abbr: "SÄNG", w: 100, h: 140, wcm: 140, hcm: 200, track: false },
  { key: "bord", label: "Bord", abbr: "BORD", w: 90, h: 70, wcm: 120, hcm: 80, track: false },
  { key: "skrivbord", label: "Skrivbord", abbr: "SKRIVB", w: 90, h: 45, wcm: 120, hcm: 60, track: false },
  { key: "blomlada", label: "Blomlåda", abbr: "BL", w: 60, h: 20, wcm: 80, hcm: 25, track: false },
  { key: "byra", label: "Byrå", abbr: "BYRÅ", w: 70, h: 35, wcm: 90, hcm: 45, track: false },
  { key: "soffbord", label: "Soffbord", abbr: "SOFFBORD", w: 80, h: 40, wcm: 120, hcm: 60, track: false },
  { key: "tvbank", label: "TV-bänk", abbr: "TV-BÄNK", w: 120, h: 28, wcm: 180, hcm: 42, track: false },
  { key: "vitrinskap", label: "Vitrinskåp", abbr: "VITRIN", w: 70, h: 30, wcm: 100, hcm: 40, track: false },
  { key: "radio", label: "Radio", abbr: "RADIO", w: 45, h: 34, wcm: 60, hcm: 45, track: true }, // floor-standing radio cabinet
  { key: "hatthylla", label: "Hatthylla", abbr: "HATT", w: 80, h: 20, wcm: 100, hcm: 28, track: false },
  { key: "vagghylla", label: "Vägghylla", abbr: "HYLLA", w: 60, h: 16, wcm: 80, hcm: 20, track: false },
];

// Kinds hung on the wall at an adjustable height (Fixture.elev)
export const ELEV_KINDS = new Set(["vagghylla"]);
export const DEFAULT_ELEV = 150; // cm
export const elevOf = (f: Fixture): number => f.elev ?? DEFAULT_ELEV;

export const kindOf = (key: string): FixtureKind | undefined => FIXTURE_KINDS.find((k) => k.key === key);
export const abbrForFixture = (f: Fixture): string => kindOf(f.kind)?.abbr || f.label.slice(0, 3).toUpperCase();

export const newId = (): string => Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
export const snap = (n: number): number => Math.round(n / GRID) * GRID;

export const blankFixture = (kind: string, x: number, y: number, scale = 0): Fixture => {
  const k = kindOf(kind);
  const w = scale > 0 && k ? Math.max(6, Math.round((k.wcm / 100) * scale)) : (k?.w || 50);
  const h = scale > 0 && k ? Math.max(6, Math.round((k.hcm / 100) * scale)) : (k?.h || 50);
  return {
    id: newId(),
    kind,
    label: k?.label || "Pryl",
    x: snap(x - w / 2),
    y: snap(y - h / 2),
    w,
    h,
    rot: 0,
    brand: "",
    model: "",
    lastReplaced: "",
    intervalMonths: "",
    notes: "",
  };
};

const num = (v: any, d = 0): number => (Number.isFinite(Number(v)) ? Number(v) : d);

export const normalizeHome = (h: any): Home => ({
  planUrl: h?.planUrl ?? "",
  planRot: num(h?.planRot),
  planHidden: !!h?.planHidden,
  walls: Array.isArray(h?.walls)
    ? h.walls.map((w: any) => ({ id: w?.id ?? newId(), x1: num(w?.x1), y1: num(w?.y1), x2: num(w?.x2), y2: num(w?.y2), balcony: !!w?.balcony, door: !!w?.door }))
    : [],
  windows: Array.isArray(h?.windows)
    ? h.windows.map((w: any) => ({ id: w?.id ?? newId(), x1: num(w?.x1), y1: num(w?.y1), x2: num(w?.x2), y2: num(w?.y2) }))
    : [],
  wallColors: h?.wallColors && typeof h.wallColors === "object"
    ? Object.fromEntries(
        Object.entries(h.wallColors as Record<string, any>).map(([id, c]) => {
          const p = normPaint(c?.p), m = normPaint(c?.m);
          return [id, { ...(p ? { p } : {}), ...(m ? { m } : {}) }];
        }),
      )
    : {},
  fixtures: Array.isArray(h?.fixtures)
    ? h.fixtures.map((f: any) => ({
        id: f?.id ?? newId(),
        kind: f?.kind ?? "garderob",
        label: f?.label ?? (kindOf(f?.kind)?.label || "Pryl"),
        x: num(f?.x), y: num(f?.y), w: num(f?.w, 50), h: num(f?.h, 50),
        rot: num(f?.rot),
        ...(typeof f?.color === "string" ? { color: f.color } : {}),
        ...(typeof f?.topColor === "string" ? { topColor: f.topColor } : {}),
        brand: f?.brand ?? "", model: f?.model ?? "",
        lastReplaced: f?.lastReplaced ?? "",
        intervalMonths: f?.intervalMonths != null ? String(f.intervalMonths) : "",
        notes: f?.notes ?? "",
      }))
    : [],
  labels: Array.isArray(h?.labels)
    ? h.labels.map((l: any) => ({ id: l?.id ?? newId(), text: l?.text ?? "", x: num(l?.x), y: num(l?.y) }))
    : [],
  scale: num(h?.scale),
  measures: Array.isArray(h?.measures)
    ? h.measures.map((m: any) => ({
        id: m?.id ?? newId(),
        x1: num(m?.x1), y1: num(m?.y1), x2: num(m?.x2), y2: num(m?.y2),
        ...(m?.label ? { label: String(m.label) } : {}),
      }))
    : [],
  measuresHidden: !!h?.measuresHidden,
  actualArea: num(h?.actualArea),
  rooms: Array.isArray(h?.rooms)
    ? h.rooms.map((r: any) => ({
        id: r?.id ?? newId(),
        name: r?.name ?? "",
        items: Array.isArray(r?.items)
          ? r.items
              .filter((i: any) => i && typeof i.value === "string" && ["image", "color", "note"].includes(i.kind))
              .map((i: any) => ({ id: i.id ?? newId(), kind: i.kind, value: i.value, ...(i.link ? { link: String(i.link) } : {}) }))
          : [],
      }))
    : [],
});

// ── per-room area detection ──
// A closed region found in the wall+door network, with its area in design units².
export interface DetectedRoom {
  id: string;
  points: { x: number; y: number }[];
  cx: number; cy: number; // centroid (for the area label)
  areaUnits: number;      // enclosed area in design units² (always positive)
  balcony: boolean;       // boundary is mostly balcony walls → it's a balcony
}

// Points closer than this (design units) are treated as the same corner, and a
// wall end within this distance of another wall counts as touching it. Keeps
// hand-drawn walls that don't quite meet (and T-junctions) connected.
const VERT_TOL = 10;

// Build a planar graph from the walls+doors network:
//   1. Merge near-coincident endpoints into shared corners.
//   2. Add a vertex at every wall crossing (X-junction).
//   3. Split each wall at every vertex lying on it — so a wall that meets another
//      one mid-span (a T-junction) actually connects in the graph.
const planarize = (walls: Wall[]): { verts: { x: number; y: number }[]; adj: number[][]; balconyEdge: Map<string, boolean> } => {
  const raw = walls
    .filter((w) => Math.hypot(w.x2 - w.x1, w.y2 - w.y1) > 1)
    .map((w) => ({ ax: w.x1, ay: w.y1, bx: w.x2, by: w.y2, balc: !!w.balcony }));

  const verts: { x: number; y: number }[] = [];
  const vertAt = (x: number, y: number): number => {
    for (let i = 0; i < verts.length; i++) if (Math.hypot(verts[i].x - x, verts[i].y - y) <= VERT_TOL) return i;
    verts.push({ x, y });
    return verts.length - 1;
  };
  for (const s of raw) { vertAt(s.ax, s.ay); vertAt(s.bx, s.by); }

  for (let i = 0; i < raw.length; i++) {
    for (let j = i + 1; j < raw.length; j++) {
      const a = raw[i], b = raw[j];
      const rx = a.bx - a.ax, ry = a.by - a.ay, sx = b.bx - b.ax, sy = b.by - b.ay;
      const d = rx * sy - ry * sx;
      if (Math.abs(d) < 1e-9) continue; // parallel
      const t = ((b.ax - a.ax) * sy - (b.ay - a.ay) * sx) / d;
      const u = ((b.ax - a.ax) * ry - (b.ay - a.ay) * rx) / d;
      if (t > 0.01 && t < 0.99 && u > 0.01 && u < 0.99) vertAt(a.ax + t * rx, a.ay + t * ry);
    }
  }

  const adj: number[][] = verts.map(() => []);
  const seenEdge = new Set<string>();
  const balconyEdge = new Map<string, boolean>(); // edge is balcony only if every wall on it is
  const link = (a: number, b: number, balc: boolean) => {
    if (a === b) return;
    const key = a < b ? `${a}-${b}` : `${b}-${a}`;
    balconyEdge.set(key, balconyEdge.has(key) ? (balconyEdge.get(key)! && balc) : balc);
    if (seenEdge.has(key)) return;
    seenEdge.add(key);
    adj[a].push(b); adj[b].push(a);
  };
  for (const s of raw) {
    const dx = s.bx - s.ax, dy = s.by - s.ay, L2 = dx * dx + dy * dy || 1;
    const on: { t: number; v: number }[] = [];
    for (let v = 0; v < verts.length; v++) {
      const p = verts[v];
      const t = ((p.x - s.ax) * dx + (p.y - s.ay) * dy) / L2;
      if (t < -0.002 || t > 1.002) continue;
      if (Math.hypot(p.x - (s.ax + t * dx), p.y - (s.ay + t * dy)) <= VERT_TOL) on.push({ t, v });
    }
    on.sort((p, q) => p.t - q.t);
    for (let k = 0; k + 1 < on.length; k++) link(on[k].v, on[k + 1].v, s.balc);
  }
  return { verts, adj, balconyEdge };
};

// Open wall ends: graph vertices touched by only one segment. These are the
// gaps that keep a room from closing — a door/wall is needed there.
export const openWallEnds = (walls: Wall[]): { x: number; y: number }[] => {
  const { verts, adj } = planarize(walls);
  return verts.filter((_, i) => adj[i].length === 1);
};

// Drawn endpoints for ordinary walls: extend any end that meets another ordinary
// wall (its endpoint or its body/T-junction) outward by half the wall width, so
// the two walls overlap into a clean corner instead of leaving a notch. Ends
// that meet nothing are left as-is, so real gaps stay visible. Only affects
// rendering — the stored walls (and the m² calc) are unchanged.
export const extendedWalls = (walls: Wall[], hw = 5): Record<string, { x1: number; y1: number; x2: number; y2: number }> => {
  const ord = walls.filter((w) => !w.balcony && !w.door && Math.hypot(w.x2 - w.x1, w.y2 - w.y1) > 1);
  const meetsOther = (x: number, y: number, selfId: string): boolean => {
    for (const w of ord) {
      if (w.id === selfId) continue;
      if (Math.hypot(w.x1 - x, w.y1 - y) <= VERT_TOL || Math.hypot(w.x2 - x, w.y2 - y) <= VERT_TOL) return true;
      const dx = w.x2 - w.x1, dy = w.y2 - w.y1, L2 = dx * dx + dy * dy || 1;
      const t = ((x - w.x1) * dx + (y - w.y1) * dy) / L2;
      if (t < 0 || t > 1) continue;
      if (Math.hypot(w.x1 + t * dx - x, w.y1 + t * dy - y) <= VERT_TOL) return true;
    }
    return false;
  };
  const out: Record<string, { x1: number; y1: number; x2: number; y2: number }> = {};
  for (const w of ord) {
    const len = Math.hypot(w.x2 - w.x1, w.y2 - w.y1) || 1;
    const ux = (w.x2 - w.x1) / len, uy = (w.y2 - w.y1) / len;
    let { x1, y1, x2, y2 } = w;
    if (meetsOther(w.x1, w.y1, w.id)) { x1 -= ux * hw; y1 -= uy * hw; }
    if (meetsOther(w.x2, w.y2, w.id)) { x2 += ux * hw; y2 += uy * hw; }
    out[w.id] = { x1, y1, x2, y2 };
  }
  return out;
};

// Find the enclosed rooms in the walls+doors network and their areas.
// Walk half-edges, always turning as far clockwise as possible — this traces
// every bounded face once. Interior faces come out counter-clockwise (in math
// coords); the outer face(s) come out the other way and are discarded. Doors
// count as boundary segments, so a doorway closes its room. Works per connected
// component, so detached outlines each contribute their own rooms.
export const detectRooms = (walls: Wall[]): DetectedRoom[] => {
  const { verts, adj, balconyEdge } = planarize(walls);
  const n = verts.length;
  if (n < 3) return [];
  const edgeKey = (a: number, b: number) => (a < b ? `${a}-${b}` : `${b}-${a}`);

  // Angle from vertex `from` to `to` in math coords (y up, so negate y).
  const ang = (from: number, to: number) => Math.atan2(-(verts[to].y - verts[from].y), verts[to].x - verts[from].x);
  const sortedNb = adj.map((nbs, v) => [...nbs].sort((p, q) => ang(v, p) - ang(v, q)));

  // After arriving u→v, leave on the edge one step clockwise from the way back.
  const nextHE = (u: number, v: number): [number, number] => {
    const nbs = sortedNb[v];
    const i = nbs.indexOf(u);
    return [v, nbs[(i - 1 + nbs.length) % nbs.length]];
  };

  const used = new Set<string>();
  const he = (a: number, b: number) => `${a}>${b}`;
  const rooms: DetectedRoom[] = [];

  for (let a = 0; a < n; a++) {
    for (const b0 of adj[a]) {
      if (used.has(he(a, b0))) continue;
      const face: number[] = [];
      let u = a, v = b0, guard = 0;
      while (!used.has(he(u, v)) && guard++ < 100000) {
        used.add(he(u, v));
        face.push(u);
        [u, v] = nextHE(u, v);
      }
      if (face.length < 3) continue;
      // Screen-coord shoelace: interior faces (CCW in math coords) are negative here.
      let area2 = 0, cx = 0, cy = 0;
      for (let i = 0; i < face.length; i++) {
        const p = verts[face[i]], q = verts[face[(i + 1) % face.length]];
        const cross = p.x * q.y - q.x * p.y;
        area2 += cross;
        cx += (p.x + q.x) * cross;
        cy += (p.y + q.y) * cross;
      }
      if (area2 >= -1) continue; // outer face or sliver — skip
      const areaUnits = -area2 / 2;
      if (areaUnits < 150) continue; // drop tiny slivers from near-misses
      // balcony if a good share of the perimeter (by length) is balcony walls
      let balcLen = 0, periLen = 0;
      for (let i = 0; i < face.length; i++) {
        const p = verts[face[i]], q = verts[face[(i + 1) % face.length]];
        const L = Math.hypot(q.x - p.x, q.y - p.y);
        periLen += L;
        if (balconyEdge.get(edgeKey(face[i], face[(i + 1) % face.length]))) balcLen += L;
      }
      rooms.push({
        id: `room-${Math.round(cx)}-${Math.round(cy)}`,
        points: face.map((i) => verts[i]),
        cx: cx / (3 * area2), cy: cy / (3 * area2),
        areaUnits,
        balcony: periLen > 0 && balcLen / periLen >= 0.25,
      });
    }
  }
  return rooms;
};

// ── replacement reminders ──
export const dueDate = (f: Fixture): Date | null => {
  const months = parseInt(f.intervalMonths, 10);
  if (!f.lastReplaced || !months) return null;
  const base = f.lastReplaced.length === 7 ? `${f.lastReplaced}-01` : f.lastReplaced;
  const d = new Date(base);
  if (isNaN(d.getTime())) return null;
  d.setMonth(d.getMonth() + months);
  return d;
};

export type DueStatus = "none" | "ok" | "soon" | "overdue";

export const replacementStatus = (f: Fixture): DueStatus => {
  const due = dueDate(f);
  if (!due) return "none";
  const now = new Date();
  const soon = new Date();
  soon.setDate(soon.getDate() + 30);
  if (due < now) return "overdue";
  if (due < soon) return "soon";
  return "ok";
};

// Downscale a picked image to a compact JPEG data URL.
export const fileToDataUrl = (file: File, maxDim = 1400, quality = 0.82): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => {
      const img = new Image();
      img.onerror = reject;
      img.onload = () => {
        const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext("2d");
        if (!ctx) { reject(new Error("no-ctx")); return; }
        ctx.fillStyle = "#fff";
        ctx.fillRect(0, 0, w, h);
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.src = reader.result as string;
    };
    reader.readAsDataURL(file);
  });
